import { cn } from "@/lib/utils";
import type { CycleHistoryRow } from "@payrollmart-invoice/shared";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Activity } from "lucide-react";

interface CycleComparisonTableProps {
	rows: CycleHistoryRow[];
}

/**
 * "How this cycle compares" table. Renders the trailing N months oldest→newest
 * with the current cycle highlighted and pilled as "in progress". Revenue,
 * issues, and days-to-close all come from the server-assembled payload —
 * formatting is the only local concern.
 */
export function CycleComparisonTable({ rows }: CycleComparisonTableProps) {
	// Newest first reads more naturally as a "how does today compare to last
	// month" narrative, so we reverse the oldest→newest server payload here.
	const display = [...rows].reverse();
	return (
		<div
			className="rounded-lg border border-[#e5e7eb] bg-white"
			data-testid="cycle-comparison-table"
		>
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<div className="flex items-center gap-2">
					<Activity className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
					<h2 className="text-sm font-semibold text-[#1e1e20]">How this cycle compares</h2>
				</div>
				<span className="text-[11px] text-[#9ca3af]">Last {rows.length} months</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-[#f3f4f6] bg-[#f8f9fb] text-xs font-medium text-[#6b7280]">
							<th className="px-4 py-2 text-left">Cycle</th>
							<th className="px-4 py-2 text-right">Invoices</th>
							<th className="px-4 py-2 text-right">Revenue</th>
							<th className="px-4 py-2 text-right">Issues resolved</th>
							<th className="px-4 py-2 text-right">Days to close</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-[#f3f4f6]">
						{display.map((row) => (
							<tr
								key={`${row.year}-${row.month}`}
								data-testid="cycle-comparison-row"
								data-is-current={row.isCurrent}
								className={cn(row.isCurrent ? "bg-[#f3f4f6]" : "hover:bg-[#f9fafb]")}
							>
								<td className="px-4 py-2.5">
									<div className="flex items-center gap-2">
										<span className="font-medium text-[#1e1e20]">{row.monthLabel}</span>
										{row.isCurrent && (
											<span className="rounded-full bg-[#eef2ff] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[#4f46e5]">
												in progress
											</span>
										)}
									</div>
								</td>
								<td className="px-4 py-2.5 text-right tabular-nums text-[#1e1e20]">
									{row.invoiceCount}
								</td>
								<td className="px-4 py-2.5 text-right tabular-nums text-[#1e1e20]">
									{row.isCurrent && row.invoiceCount === 0 ? "—" : formatDollars(row.revenueCents)}
								</td>
								<td className="px-4 py-2.5 text-right tabular-nums text-[#6b7280]">
									{row.issuesResolved}
								</td>
								<td className="px-4 py-2.5 text-right tabular-nums text-[#6b7280]">
									{formatDaysToClose(row.daysToClose, row.isCurrent)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

/**
 * Render the `daysToClose` column. Positive = pushed after books closed,
 * 0 = same day, negative = pushed before month end (current cycle mid-push).
 * Null = nothing pushed yet; current cycles surface this as "in progress",
 * closed months surface it as em-dash so the column doesn't jitter.
 */
function formatDaysToClose(days: number | null, isCurrent: boolean): string {
	if (days === null) return isCurrent ? "in progress" : "—";
	if (days === 0) return "0";
	if (days < 0) return `${days}`;
	return `+${days}`;
}
